import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Clock, DollarSign, Activity, ChevronDown, ChevronUp, Battery, User, Calendar } from "lucide-react";
import { Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Line, ComposedChart } from "recharts";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider";
import { useTimezone } from "../../lib/context/TimezoneProvider";
import RefreshButton from "../../lib/components/RefreshButton";
import Pagination from "../../lib/components/Pagination/Pagination";
import DateRangeToolbar from "../fleet_dashboard/DateRangeToolbar";
import { fetchSessionHistory } from "./hooks/api";
import {
  SESSION_FIELDS,
  CHART_BAR_COLOR,
  CHART_LINE_COLOR,
  formatSessionTime,
  formatSessionValue,
  getEnergyColor,
} from "./constants";
import "./styles.scss";

dayjs.extend(utc);
dayjs.extend(tz);
dayjs.extend(customParseFormat);

const DATE_FORMAT_DD_MM_YYYY = "DD-MM-YYYY";

function SessionCard({ session, timezone, index, bidiSupported }) {
  const [expanded, setExpanded] = useState(false);
  const energyColor = getEnergyColor(session.aggEnergyValue);
  const visibleFields = useMemo(() => SESSION_FIELDS.filter(f => !f.bidiOnly || bidiSupported), [bidiSupported]);

  return (
    <motion.div
      className="session-card"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3), duration: 0.25 }}
      style={{ borderLeftColor: energyColor }}
    >
      <div
        className="session-card__header"
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(prev => !prev)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") setExpanded(prev => !prev);
        }}
      >
        <div className="session-card__summary">
          <div className="session-card__date">
            <Calendar size={14} />
            <span>{formatSessionTime(session.startTime, timezone)}</span>
          </div>
          <div className="session-card__chips">
            <span className="session-chip session-chip--duration">
              <Clock size={12} />
              {session.sessionDurationFormatted || "—"}
            </span>
            <span className="session-chip session-chip--energy">
              <Zap size={12} />
              {session.aggEnergyValue != null ? `${session.aggEnergyValue} kWh` : "—"}
            </span>
            {session.sessionCost != null && session.sessionCost > 0 && (
              <span className="session-chip session-chip--cost">
                <DollarSign size={12} />${session.sessionCost}
              </span>
            )}
            {session.authType && (
              <span className="session-chip session-chip--auth">
                <User size={12} />
                {session.authType}
              </span>
            )}
          </div>
        </div>
        <div className="session-card__toggle">{expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div
            className="session-card__details"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="session-card__grid">
              {visibleFields.map(field => {
                let value = session[field.key];
                // Format timestamps
                if (field.key === "startTime" || field.key === "endTime") {
                  value = formatSessionTime(value, timezone);
                } else {
                  value = formatSessionValue(field, value);
                }
                return (
                  <div key={field.key} className="session-card__field">
                    <span className="session-card__label">{field.label}</span>
                    <span className="session-card__value">{value}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

SessionCard.propTypes = {
  session: PropTypes.object.isRequired,
  timezone: PropTypes.string,
  index: PropTypes.number.isRequired,
  bidiSupported: PropTypes.bool,
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="session-chart-tooltip">
      <div className="session-chart-tooltip__date">{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} className="session-chart-tooltip__item" style={{ color: p.color }}>
          <span>{p.dataKey === "energy" ? "Energy" : "Sessions"}</span>
          <span className="session-chart-tooltip__val">{p.dataKey === "energy" ? `${p.value} kWh` : p.value}</span>
        </div>
      ))}
    </div>
  );
}

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
};

export default function SessionHistory({ activePreset, onPresetChange, onCustomChange, onRefresh, bidiSupported }) {
  const { serialNumber, dateRange, siteId } = useGlobalFilters();
  const { effectiveTimezone } = useTimezone();
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const lastRequestKeyRef = useRef("");

  const totalItems = sessions.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / rowsPerPage) : 0;
  const startIndex = page * rowsPerPage;
  const paginatedSessions = sessions.slice(startIndex, startIndex + rowsPerPage);

  // Build daily chart data from sessions
  const chartData = useMemo(() => {
    if (!sessions.length) return [];
    const dailyMap = {};
    sessions.forEach(s => {
      if (!s.startTime) return;
      const cleaned = s.startTime.includes("[") ? s.startTime.substring(0, s.startTime.indexOf("[")) : s.startTime;
      const d = effectiveTimezone ? dayjs(cleaned).tz(effectiveTimezone) : dayjs(cleaned);
      const key = d.format("MMM D");
      if (!dailyMap[key]) dailyMap[key] = { date: key, energy: 0, count: 0, sortKey: d.valueOf() };
      dailyMap[key].energy += s.aggEnergyValue || 0;
      dailyMap[key].count += 1;
    });
    return Object.values(dailyMap)
      .sort((a, b) => a.sortKey - b.sortKey)
      .map(d => ({ date: d.date, energy: Math.round(d.energy * 100) / 100, count: d.count }));
  }, [sessions, effectiveTimezone]);

  useEffect(() => {
    if (page !== 0) setPage(0);
  }, [serialNumber, siteId, dateRange]);

  const loadSessions = useCallback(async () => {
    if (!serialNumber || !siteId || !dateRange?.[0] || !dateRange?.[1]) {
      lastRequestKeyRef.current = "";
      setLoading(false);
      setSessions([]);
      setSummary(null);
      return;
    }

    const tz = effectiveTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startDate = dayjs(dateRange[0]).format(DATE_FORMAT_DD_MM_YYYY);
    const endDate = dayjs(dateRange[1]).format(DATE_FORMAT_DD_MM_YYYY);
    // Fetch a large page to get all sessions for the date range (for charts & KPIs)
    const fetchLimit = 100;

    const requestKey = JSON.stringify({ serialNumber, siteId, startDate, endDate, tz });
    if (lastRequestKeyRef.current === requestKey) return;
    lastRequestKeyRef.current = requestKey;

    setLoading(true);
    try {
      const res = await fetchSessionHistory(serialNumber, {
        siteId,
        startDate,
        endDate,
        timezone: tz,
        offset: 0,
        limit: fetchLimit,
      });
      setSessions(res?.sessions || []);
      setSummary(res?.summary || null);
      setHasMore(res?.hasMore || false);
    } catch {
      lastRequestKeyRef.current = "";
      setSessions([]);
      setSummary(null);
    }
    setLoading(false);
  }, [serialNumber, siteId, dateRange, effectiveTimezone]);

  useEffect(() => {
    loadSessions();
    return () => {
      lastRequestKeyRef.current = "";
    };
  }, [loadSessions]);

  const handlePageChange = newPage => setPage(newPage);
  const handleRowsPerPageChange = newRows => {
    setRowsPerPage(newRows);
    setPage(0);
  };

  const kpiCards = useMemo(() => {
    if (!summary) return [];
    return [
      {
        label: "Total Sessions",
        value: summary.totalSessions,
        icon: Activity,
        color: "blue",
      },
      {
        label: "Energy Consumed",
        value: `${summary.totalEnergyKwh} kWh`,
        icon: Zap,
        color: "green",
      },
      {
        label: "Total Duration",
        value: summary.totalDurationFormatted || "—",
        icon: Clock,
        color: "purple",
      },
      {
        label: "Avg per Session",
        value: `${summary.avgEnergyPerSession} kWh`,
        sub: summary.avgSessionDurationFormatted ? `~${summary.avgSessionDurationFormatted}` : null,
        icon: Battery,
        color: "orange",
      },
    ];
  }, [summary]);

  return (
    <div className="session-history">
      {/* Toolbar */}
      <div className="session-history__toolbar">
        <DateRangeToolbar
          activePreset={activePreset}
          onPresetChange={onPresetChange}
          onCustomChange={onCustomChange}
          showTimezone
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <RefreshButton
            onClick={() => {
              lastRequestKeyRef.current = "";
              if (onRefresh) onRefresh();
              else loadSessions();
            }}
            tooltip="Refresh sessions"
          />
        </Box>
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="session-history__skeleton">
          <div className="session-history__kpi-row">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="skel skel-kpi" style={{ animationDelay: `${i * 0.1}s` }} />
            ))}
          </div>
          <div className="skel skel-chart" />
          {[0, 1, 2].map(i => (
            <div key={i} className="skel skel-card" style={{ animationDelay: `${i * 0.1}s` }} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && sessions.length === 0 && (
        <motion.div className="session-history__empty" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Battery size={40} className="session-history__empty-icon" />
          <h3>No Charging Sessions</h3>
          <p>No sessions found for the selected date range. Try adjusting the date filter.</p>
        </motion.div>
      )}

      {/* Content */}
      {!loading && sessions.length > 0 && (
        <>
          {/* KPI Cards */}
          <div className="session-history__kpi-row">
            {kpiCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={card.label}
                  className="session-kpi"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06, duration: 0.3 }}
                >
                  <div className={`session-kpi__icon session-kpi__icon--${card.color}`}>
                    <Icon size={20} />
                  </div>
                  <div className="session-kpi__content">
                    <span className="session-kpi__label">{card.label}</span>
                    <span className="session-kpi__value">{card.value}</span>
                    {card.sub && <span className="session-kpi__sub">{card.sub}</span>}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Chart */}
          {chartData.length > 1 && (
            <motion.div
              className="session-history__chart-card"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.3 }}
            >
              <div className="session-history__chart-header">
                <h3>Daily Energy & Sessions</h3>
                <div className="session-history__chart-legend">
                  <span className="session-history__legend-item">
                    <span className="session-history__legend-swatch" style={{ background: CHART_BAR_COLOR }} />
                    Energy (kWh)
                  </span>
                  <span className="session-history__legend-item">
                    <span
                      className="session-history__legend-swatch session-history__legend-swatch--line"
                      style={{ background: CHART_LINE_COLOR }}
                    />
                    Sessions
                  </span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #e5e7eb)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-text-secondary, #9ca3af)" />
                  <YAxis
                    yAxisId="energy"
                    tick={{ fontSize: 11 }}
                    stroke="var(--color-text-secondary, #9ca3af)"
                    label={{ value: "kWh", angle: -90, position: "insideLeft", style: { fontSize: 11 } }}
                  />
                  <YAxis
                    yAxisId="count"
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    stroke="var(--color-text-secondary, #9ca3af)"
                    allowDecimals={false}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar yAxisId="energy" dataKey="energy" fill={CHART_BAR_COLOR} radius={[4, 4, 0, 0]} barSize={28} />
                  <Line
                    yAxisId="count"
                    type="monotone"
                    dataKey="count"
                    stroke={CHART_LINE_COLOR}
                    strokeWidth={2}
                    dot={{ r: 3, fill: CHART_LINE_COLOR }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </motion.div>
          )}

          {/* Session Cards */}
          <div className="session-history__list">
            <div className="session-history__list-header">
              <h3>
                Sessions ({sessions.length}
                {hasMore ? "+" : ""})
              </h3>
            </div>
            {paginatedSessions.map((session, i) => (
              <SessionCard
                key={session.id || i}
                session={session}
                timezone={effectiveTimezone}
                index={i}
                bidiSupported={bidiSupported}
              />
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            totalPages={totalPages}
            currentPage={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            totalItems={totalItems}
            itemLabel="sessions"
          />
        </>
      )}
    </div>
  );
}

SessionHistory.propTypes = {
  activePreset: PropTypes.string,
  onPresetChange: PropTypes.func,
  onCustomChange: PropTypes.func,
  onRefresh: PropTypes.func,
  bidiSupported: PropTypes.bool,
};
